import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import {
  ChangeContext,
  NgxSliderModule,
  Options,
} from "@angular-slider/ngx-slider";
import { DateRangeUpdated } from "../../../store/actions/dashboard.actions";
import { select, Store } from "@ngrx/store";
import { faCalendarDay } from "@fortawesome/free-solid-svg-icons";
import { NbCalendarRange, NbCalendarRangeModule } from "@nebular/theme";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { filter, map, takeUntil } from "rxjs/operators";
import { AppState } from "../../../store/reducers";
import { ViewConfigViewModel } from "@helix/client";
import ElementQueries from "css-element-queries/src/ElementQueries";
import { TimeSliderService } from "../../../services/time-slider.service";
import { DateTime } from "luxon";
import {
  getDateOneMillisecondBeforeMidnight,
  getDateWithoutTime,
} from "../../../helpers";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { AsyncPipe, NgIf } from "@angular/common";

export interface MinMaxDateRange {
  minDate: DateTime;
  maxDate: DateTime;
}

@Component({
  selector: "ngx-time-selector",
  templateUrl: "./time-selector.component.html",
  styleUrls: ["./time-selector.component.scss"],
  imports: [
    NgxSliderModule,
    FontAwesomeModule,
    NgIf,
    NbCalendarRangeModule,
    AsyncPipe,
  ],
})
export class TimeSelectorComponent implements OnInit, OnDestroy, AfterViewInit {
  private componentDestroyed$: Subject<boolean> = new Subject();

  @Input() selectedDateRangeMin$: Observable<DateTime>;
  @Input() selectedDateRangeMax$: Observable<DateTime>;
  @Input() viewConfig$: Observable<ViewConfigViewModel>;
  @Input() integrateWithStore: boolean = true;

  @ViewChild("timeSlider", { static: true }) timeSliderElement: any;

  @Output() dateRangeChangedEmitter: EventEmitter<MinMaxDateRange> =
    new EventEmitter<MinMaxDateRange>();

  manualRefresh: EventEmitter<void> = new EventEmitter<void>();
  faCalendarDay: any = faCalendarDay;
  showCalendar: boolean = false;
  timeSelectorIsLoading$ = new BehaviorSubject<boolean>(true);
  calendarRange$: Observable<NbCalendarRange<Date>>;
  calendarViewStartDate$: Observable<Date>;
  timeSliderSteps: DateTime[];
  timeSliderOptions$: BehaviorSubject<Options> = new BehaviorSubject<Options>({
    ceil: 1,
    floor: 0,
    animate: false,
  });
  showTimeSlider$: Observable<boolean>;

  constructor(
    private store: Store<AppState>,
    private timeSliderService: TimeSliderService,
  ) {}

  ngAfterViewInit() {
    ElementQueries.listen();
    ElementQueries.init();
  }

  ngOnInit() {
    this.calendarViewStartDate$ = this.timeSliderService
      .currentStartDate()
      .pipe(map((x) => x.toJSDate()));
    this.calendarRange$ = this.timeSliderService.currentDateRange().pipe(
      map((x) => ({
        start: x.startDate.toJSDate(),
        end: x.endDate.toJSDate(),
      })),
    );

    if (this.integrateWithStore) {
      this.selectedDateRangeMin$ = this.timeSliderService
        .currentStartDate()
        .pipe(map((x) => x));
      this.selectedDateRangeMax$ = this.timeSliderService
        .currentEndDate()
        .pipe(map((x) => x));

      this.viewConfig$ = this.store.pipe(
        select((appState: AppState) => appState.dashboard.viewConfig),
        filter((x) => x !== null),
      );
    }

    this.viewConfig$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((viewConfig) => {
        let defaultDayRange = viewConfig.defaultDateRangeDays;
        let dateRangeMax = DateTime.now();
        let dateRangeMin = viewConfig.startingDate;

        this.timeSelectorIsLoading$.next(false);
        this.timeSliderSteps = getDateSteps(dateRangeMin, dateRangeMax);

        if (this.integrateWithStore) {
          const initialStartDateMillis = getDateWithoutTime(dateRangeMax).minus(
            { days: defaultDayRange },
          );
          const initialEndDateMillis = dateRangeMax.set({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 999,
          });
          this.store.dispatch(
            new DateRangeUpdated({
              minDate: initialStartDateMillis,
              maxDate: initialEndDateMillis,
            }),
          );
        }

        this.initializeTimeSliderOptions();
        setTimeout(() => {
          this.manualRefresh.emit();
        }, 0);
      });

    this.showTimeSlider$ = combineLatest([
      this.timeSelectorIsLoading$,
      this.store.select((state: AppState) => state.dashboard.showTimeSelector),
    ]).pipe(map(([isLoading, isShown]) => !isLoading && isShown));
  }

  calendarRangeChange(event: NbCalendarRange<Date>): void {
    if (event.start && event.end) {
      this.dateRangeChanged(event.start.getTime(), event.end.getTime());
      this.showCalendar = false;
    }
  }

  initializeTimeSliderOptions() {
    this.timeSliderOptions$.next({
      draggableRange: true,
      stepsArray: this.timeSliderSteps.map((date: DateTime) => ({
        value: date.toMillis(),
      })),
      enforceRange: false,
      enforceStep: false,
      translate: (millis: number): string =>
        DateTime.fromMillis(millis).toLocaleString(),
      animate: false,
    });
  }

  timeSliderChanged(event: ChangeContext): void {
    this.dateRangeChanged(event.value, event.highValue);
  }

  clickedInsideCalendar($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
  }

  @HostListener("document:click", ["$event"]) clickedOutside() {
    if (this.showCalendar) {
      this.showCalendar = false;
    }
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  private dateRangeChanged(
    startDateMilliseconds: number,
    endDateMilliseconds: number,
  ): void {
    const minDate = getDateWithoutTime(
      DateTime.fromMillis(startDateMilliseconds),
    );
    const maxDate = getDateOneMillisecondBeforeMidnight(
      DateTime.fromMillis(endDateMilliseconds),
    );
    this.dateRangeChangedEmitter.emit({ minDate, maxDate });
    if (this.integrateWithStore) {
      this.store.dispatch(new DateRangeUpdated({ minDate, maxDate }));
    }
  }
}

function getDateSteps(start: DateTime, end: DateTime): DateTime[] {
  if (start > end) {
    return getDateSteps(end, start);
  }

  const dateSteps: DateTime[] = [];
  let currDate = start;
  while (currDate <= end) {
    currDate = getDateWithoutTime(currDate);
    dateSteps.push(currDate);
    currDate = currDate.plus({ day: 1 });
  }

  if (dateSteps.length === 1) {
    const prevDate = dateSteps[0].minus({ days: 1 });
    dateSteps.unshift(prevDate);
  }
  if (dateSteps.length === 1) {
    const prevDate = dateSteps[0].minus({ days: 1 });
    dateSteps.unshift(prevDate);
  }
  return dateSteps;
}
